import { useMemo, useState } from 'react';
import { DEMO, useDemo } from './DemoModal';
import { buildDeliveryOrderPdf } from '../utils/pdf';
import { MATERIAL_NAME, MATERIAL_UNIT, type Yard } from '../types';
import { money, todayISO } from '../utils/format';

export interface DeliveryOrderPrefill {
  yardId: string;
  material: string;
  quantity?: number;
  deliveredPerUnit: number;
  siteId?: string | null;
  siteName?: string;
  siteAddress: string;
}

export default function DeliveryOrderModal({
  prefill,
  yards,
  onClose,
}: {
  prefill: DeliveryOrderPrefill;
  yards: Yard[];
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { showDemo } = useDemo();
  const [quantity, setQuantity] = useState<number>(prefill.quantity ?? 1);
  const [date, setDate] = useState<string>(todayISO());

  const yard = useMemo(() => yards.find((y) => y.yard_id === prefill.yardId), [yards, prefill.yardId]);
  const unit = MATERIAL_UNIT[prefill.material] ?? 'unit';
  const perUnit = prefill.deliveredPerUnit;
  const total = Math.round(perUnit * quantity * 100) / 100;

  const yardAddress = [yard?.address, yard?.city, yard?.state].filter(Boolean).join(', ');

  const save = () => showDemo(DEMO.saveOrder);

  const downloadPdf = () => {
    buildDeliveryOrderPdf({
      orderNo: `DRAFT-${date}`,
      date,
      yardName: yard?.name ?? prefill.yardId,
      yardAddress: yardAddress || '—',
      material: MATERIAL_NAME[prefill.material] ?? prefill.material,
      quantity,
      unit,
      deliveredPerUnit: perUnit,
      deliveredTotal: total,
      siteName: prefill.siteName,
      siteAddress: prefill.siteAddress,
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">Delivery Order</div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="field">From yard</label>
              <div>{yard?.name ?? prefill.yardId}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {yardAddress || '—'}
              </div>
            </div>
            <div>
              <label className="field">Deliver to</label>
              <div>{prefill.siteName ?? '—'}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {prefill.siteAddress}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="field">Material</label>
              <div>{MATERIAL_NAME[prefill.material] ?? prefill.material}</div>
            </div>
            <div>
              <label className="field">Date</label>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="field">Quantity ({unit})</label>
              <input
                className="input"
                type="number"
                min={0}
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="field">Delivered $/{unit}</label>
              <div style={{ paddingTop: 8 }}>{money(perUnit)}</div>
            </div>
            <div>
              <label className="field">Total</label>
              <div style={{ paddingTop: 8, fontWeight: 700 }}>{money(total)}</div>
            </div>
          </div>

        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-secondary" onClick={downloadPdf}>
            Download PDF
          </button>
          <button className="btn btn-primary" onClick={save} disabled={quantity <= 0}>
            Save order
          </button>
        </div>
      </div>
    </div>
  );
}
